// ─────────────────────────────────────────────────────────────────
// Glass-i — Contra Voucher grid columns (Stage 6B.9C).
//
// Columns: # / Voucher No / Date / From Account / To Account / Amount
//          / Status / Posted At
//
// From/To account names live on the lines (the engine returns them
// via the standard voucher response). We resolve them from the
// lookup cache the page passes in; if the cache is cold we fall back
// to a short "—" placeholder so the row still renders.
// ─────────────────────────────────────────────────────────────────

import { Tag, Tooltip } from 'antd';
import { formatINR } from '../../../../components/common/erp-grid';
import { STATUS_COLOR, STATUS_LABEL } from './contraVoucherHelpers';

function fmtDate(v) {
  return v ? String(v).slice(0, 10) : <span className="erp-grid__muted">—</span>;
}
function fmtTs(v) {
  if (!v) return <span className="erp-grid__muted">—</span>;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return <span>{d.toISOString().slice(0, 10)} {d.toISOString().slice(11, 16)}</span>;
}

function AccountCell({ accountId, ledgerLookupRef }) {
  if (!accountId) return <span className="erp-grid__muted">—</span>;
  const cache = ledgerLookupRef && ledgerLookupRef.current;
  const row = cache ? cache.get(accountId) : null;
  if (!row) {
    return (
      <Tooltip title="Ledger details still loading">
        <span className="erp-grid__muted">…</span>
      </Tooltip>
    );
  }
  return (
    <span>
      <code className="erp-grid__code" style={{ marginRight: 6 }}>{row.accountCode}</code>
      {row.accountName}
    </span>
  );
}

export function buildContraColumns({ currentPage, pageSize, ledgerLookupRef } = {}) {
  return [
    { title: '#', key: '__sno__', width: 56, align: 'center', fixed: 'left',
      render: (_v, _r, idx) => {
        const cp = Number(currentPage) > 0 ? Number(currentPage) : 1;
        const ps = Number(pageSize)    > 0 ? Number(pageSize)    : 25;
        return <span className="erp-grid__muted">{(cp - 1) * ps + idx + 1}</span>;
      } },
    { title: 'Voucher No', dataIndex: 'voucherNo', key: 'voucherNo',
      sortField: 'voucher_no', sorter: true,
      width: 170, fixed: 'left',
      render: (v) => v
        ? <code className="erp-grid__code">{v}</code>
        : <span className="erp-grid__muted">—</span> },
    { title: 'Date', dataIndex: 'voucherDate', key: 'voucherDate',
      sortField: 'voucher_date', sorter: true,
      width: 110, dataType: 'date', render: fmtDate },
    { title: 'From Account', dataIndex: 'fromAccountId', key: 'fromAccountId',
      width: 240, ellipsis: true,
      render: (v) => <AccountCell accountId={v} ledgerLookupRef={ledgerLookupRef} /> },
    { title: 'To Account', dataIndex: 'toAccountId', key: 'toAccountId',
      width: 240, ellipsis: true,
      render: (v) => <AccountCell accountId={v} ledgerLookupRef={ledgerLookupRef} /> },
    { title: 'Amount (₹)', dataIndex: 'amount', key: 'amount',
      sortField: 'total_debit', sorter: true,
      width: 140, align: 'right',
      render: (v) => {
        const n = Number(v) || 0;
        return n === 0 ? <span className="erp-grid__muted">—</span> : formatINR(n);
      } },
    { title: 'Narration', dataIndex: 'narration', key: 'narration',
      width: 320, ellipsis: true,
      render: (v) => v || <span className="erp-grid__muted">—</span> },
    { title: 'Status', dataIndex: 'status', key: 'status',
      sortField: 'status', sorter: true,
      width: 110, align: 'center',
      dataType: 'enum',
      groupable: true, groupField: 'status',
      filters: [
        { text: 'Draft',     value: 'draft' },
        { text: 'Posted',    value: 'posted' },
        { text: 'Cancelled', value: 'cancelled' }
      ],
      filterField: 'status',
      render: (v) => (
        <Tag color={STATUS_COLOR[v] || 'default'} className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>
          {(STATUS_LABEL[v] || v || '').toUpperCase()}
        </Tag>
      ) },
    { title: 'Posted At', dataIndex: 'postedAt', key: 'postedAt',
      sortField: 'posted_at', sorter: true,
      width: 160, dataType: 'date', render: fmtTs }
  ];
}
